import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Minus, Plus, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { api, eur, apiError } from "@/lib/api";

export const CartDrawer = () => {
  const { items, open, setOpen, setQty, remove, total } = useCart();
  const [loading, setLoading] = useState(false);

  const checkout = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        items: items.map((i) => ({ product_id: i.id, quantity: i.qty })),
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(apiError(e, "Impossibile avviare il pagamento"));
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-[60] bg-forest/50 backdrop-blur-sm"
            data-testid="cart-overlay"
          />
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="fixed right-0 top-0 z-[70] flex h-full w-full max-w-md flex-col bg-bone"
            data-testid="cart-drawer"
          >
            <div className="flex items-center justify-between border-b border-line px-6 py-5">
              <h2 className="font-serif text-2xl text-forest">Il tuo carrello</h2>
              <button onClick={() => setOpen(false)} className="p-2 text-forest" data-testid="cart-close-button" aria-label="Chiudi">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-6" data-lenis-prevent>
              {items.length === 0 ? (
                <p className="mt-10 text-center text-sage" data-testid="cart-empty-message">Il carrello è vuoto. Il vivaio ti aspetta.</p>
              ) : (
                <ul className="flex flex-col gap-6">
                  {items.map((i) => (
                    <li key={i.id} className="flex gap-4" data-testid={`cart-item-${i.id}`}>
                      <div className="h-24 w-20 shrink-0 overflow-hidden rounded-t-full bg-sand">
                        <img src={i.image} alt={i.name} className="h-full w-full object-cover" />
                      </div>
                      <div className="flex flex-1 flex-col">
                        <div className="flex items-start justify-between gap-2">
                          <p className="font-serif text-lg leading-tight text-forest">{i.name}</p>
                          <button onClick={() => remove(i.id)} className="p-1 text-sage hover:text-terra" data-testid={`cart-remove-${i.id}`} aria-label="Rimuovi">
                            <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                          </button>
                        </div>
                        <p className="mt-1 text-sm text-sage">{eur(i.price)}</p>
                        <div className="mt-auto flex items-center gap-3 pt-2">
                          <button onClick={() => setQty(i.id, i.qty - 1)} className="rounded-full border border-line p-1.5 text-forest" data-testid={`cart-minus-${i.id}`} aria-label="Diminuisci">
                            <Minus className="h-3 w-3" />
                          </button>
                          <span className="min-w-6 text-center font-accent text-sm" data-testid={`cart-qty-${i.id}`}>{i.qty}</span>
                          <button onClick={() => setQty(i.id, i.qty + 1)} className="rounded-full border border-line p-1.5 text-forest" data-testid={`cart-plus-${i.id}`} aria-label="Aumenta">
                            <Plus className="h-3 w-3" />
                          </button>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {items.length > 0 && (
              <div className="border-t border-line px-6 py-6">
                <div className="flex items-center justify-between">
                  <span className="font-accent text-xs uppercase tracking-[0.2em] text-sage">Subtotale</span>
                  <span className="font-serif text-2xl text-forest" data-testid="cart-total">{eur(total)}</span>
                </div>
                <p className="mt-1 text-xs text-sage">IVA calcolata al pagamento. Spedizione concordata dopo l'ordine.</p>
                <button
                  onClick={checkout}
                  disabled={loading}
                  data-testid="checkout-button"
                  className="mt-5 flex w-full items-center justify-center gap-2 rounded-full bg-forest py-4 font-accent text-sm uppercase tracking-[0.2em] text-bone transition-transform duration-300 hover:scale-[1.02] active:scale-95 disabled:opacity-60"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  Vai al pagamento
                </button>
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};
